import { useCallback, useEffect, useRef, useState } from 'react';

import { useTheme } from '@mui/styles';
import axios from 'axios';

import type { TCaptchaConfig } from '@/utils/captchaUtils/types';
import { isCaptchaError } from '@/utils/errorUtils';
import { hasFeature } from '@/utils/featureFlag';

import type { Nullable } from '@/types/common';

export type TCaptchaHelper = ReturnType<typeof useCaptcha>;

// "normal" captcha width is 300px
const SIZE_THRESHOLD = 300;

export const useCaptcha = (action: string, error: unknown = 0) => {
  const theme = useTheme();
  const containerRef = useRef<Nullable<HTMLDivElement>>(null);
  const captchaRef = useRef<Nullable<TCaptchaConfig>>(
    !hasFeature('reCaptchaDisabled') ? { version: 'v3', action } : null,
  );
  const [failed, setFailed] = useState(false);

  // Normally, we should `ensureCaptchaApi` when using the API.
  // Although, considering that it's being called after v3 fails it's not needed here.
  const fallbackToV2 = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const parent = containerRef.current.parentElement;

    if (!parent) {
      return;
    }

    const compact = parent.offsetWidth <= SIZE_THRESHOLD;

    const widgetId = window.grecaptcha.render(containerRef.current, {
      theme: theme.palette.mode,
      sitekey: window.env.CAPTCHA_V2_KEY,
      size: compact ? 'compact' : 'normal',
      callback: () => setFailed(false),
      'error-callback': () => setFailed(true),
      'expired-callback': () => setFailed(true),
    });

    containerRef.current.setAttribute('data-compact', compact.toString());
    setFailed(true);
    captchaRef.current = {
      version: 'v2',
      widgetId,
    };
  }, [theme]);

  useEffect(() => {
    if (!captchaRef.current) {
      return;
    }

    if (axios.isAxiosError(error)) {
      if (captchaRef.current.version === 'v2') {
        window.grecaptcha.reset(captchaRef.current.widgetId);
        setFailed(true);
      } else if (isCaptchaError(error)) {
        fallbackToV2();
      }
    }
  }, [error, fallbackToV2]);

  return {
    captcha: captchaRef.current,
    captchaContainerRef: containerRef,
    captchaFailed: failed,
  };
};
